// ============================ Config ============================
const AUTO_SWAP_DISCOUNT = true;          // Auto-switch to best eligible discount code when tiers change
const AUTO_CLEAR_ON_DROP = false;         // Keep code applied even if cart drops below tier threshold
const REOPEN_DRAWER_AFTER_REDIRECT = true;
const SUBSCRIBE_SAVE_FALLBACK_PERCENT = 25; // Reverse-calc % for subscription display
const SUBSCRIBE_SAVE_EXCLUDED_HANDLES = ['sizeon', 'myofusion']; // Hide subscribe & save for these products
const HIDE_SUBSCRIBE_SAVE = true; // Set false to re-enable subscribe & save globally

// Welcome Kit settings - read from cart drawer data attributes
function getWelcomeKitConfig() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) {
    return { enabled: false, variantId: null, autoRemove: true };
  }
  const enabled = drawer.dataset.welcomeKitEnabled === 'true';
  const variantIdStr = drawer.dataset.welcomeKitVariantId || '';
  const variantId = variantIdStr ? parseInt(variantIdStr, 10) : null;
  const autoRemove = drawer.dataset.autoRemoveWelcomeKit !== 'false';
  return { enabled, variantId, autoRemove };
}

// Memorial Day BOGO in cart drawer (added 2026-05-23). Set true to re-enable drawer UI + split lines.
const BOGO_DRAWER_ENABLED = false;
const BOGO_FREE_VARIANT_IDS = [];

let __ENFORCING_WELCOME_KIT__ = false;                         // recursion guard
let __SYNCING_THRESHOLD_FREEBIES__ = false;                    // guard for threshold free gift sync
let __RECONCILING_BOGO__ = false;                              // guard for BOGO split-line reconcile
let __fetchCartInFlight__ = null;                              // coalesce overlapping fetchCart calls
window.__latestCart = window.__latestCart || null;             // cache latest cart snapshot
window.__bogoDiscounts = new Map();                            // pre-computed BOGO discounts (cents) per line key
window.__bogoState = null;                                     // pooled BOGO unit state for current cart

const BOGO_FREE_BADGE_LABEL = 'BOGO FREE';

/** True when a cart item is an explicit BOGO split-line added by reconcile. */
function isBogoFreeLine(item) {
  return !!(item.properties && item.properties['_bogo_free'] === 'true');
}

function isBogoEligibleItem(item) {
  if (!BOGO_DRAWER_ENABLED) return false;
  return !!(
    item &&
    BOGO_FREE_VARIANT_IDS.includes(item.variant_id) &&
    !(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan)
  );
}


function getBogoUnitPrice(item) {
  if (typeof item.original_price === 'number' && item.original_price > 0) return item.original_price;
  if (typeof item.price === 'number' && item.price > 0) return item.price;
  if (typeof item.original_line_price === 'number' && item.original_line_price > 0) {
    const qty = Math.max(1, Number(item.quantity) || 1);
    return Math.round(item.original_line_price / qty);
  }
  return typeof item.price === 'number' ? item.price : 0;
}

const variantSalePriceCache = new Map();
const variantCompareAtCache = new Map();

function parseVariantCompareAtCents(variant) {
  if (!variant) return 0;
  const cap = variant.compare_at_price;
  if (cap == null || cap === '') return 0;
  const n = Number(cap);
  return n > 0 ? n : 0;
}

function parseVariantSalePriceCents(variant) {
  if (!variant) return 0;
  const p = variant.price;
  if (p == null || p === '') return 0;
  const n = Number(p);
  return n > 0 ? n : 0;
}

function ensureVariantCatalogCached(item) {
  const vid = item?.variant_id;
  if (!vid) return Promise.resolve();

  const linePrice = getBogoUnitPrice(item);
  if (linePrice > 0) variantSalePriceCache.set(vid, linePrice);

  if (variantSalePriceCache.has(vid) && variantCompareAtCache.has(vid)) {
    return Promise.resolve();
  }

  if (!item.handle) return Promise.resolve();

  return fetchProductJson(item.handle).then(p => {
    const variant = (p?.variants || []).find(v => v && v.id === vid);
    const sale = parseVariantSalePriceCents(variant);
    const compareAt = parseVariantCompareAtCents(variant);
    if (sale > 0) variantSalePriceCache.set(vid, sale);
    variantCompareAtCache.set(vid, compareAt);
  });
}

async function preloadBogoVariantCatalog(items) {
  if (!BOGO_DRAWER_ENABLED) return;
  const list = (items || []).filter(i => BOGO_FREE_VARIANT_IDS.includes(i.variant_id));
  if (!list.length) return;

  const seenHandles = new Set();
  const toLoad = [];
  for (const item of list) {
    const vid = item.variant_id;
    if (variantSalePriceCache.has(vid) && variantCompareAtCache.has(vid)) continue;
    if (!item.handle || seenHandles.has(item.handle)) continue;
    seenHandles.add(item.handle);
    toLoad.push(item);
  }
  if (!toLoad.length) return;
  await Promise.all(toLoad.map(i => ensureVariantCatalogCached(i)));
}

async function preloadPromoVariantCatalog(items) {
  const promoMap = getPromoRuleMap();
  if (!promoMap.size) return;

  const seenHandles = new Set();
  const toLoad = [];
  for (const item of (items || [])) {
    const vid = item?.variant_id;
    if (!vid || !promoMap.has(String(vid))) continue;
    if (variantSalePriceCache.has(vid) && variantCompareAtCache.has(vid)) continue;
    if (!item.handle || seenHandles.has(item.handle)) continue;
    seenHandles.add(item.handle);
    toLoad.push(item);
  }
  if (!toLoad.length) return;
  await Promise.all(toLoad.map(i => ensureVariantCatalogCached(i)));
}

/** Variant selling price even when the free cart line is $0 from Cart Transform. */
function getBogoCatalogUnitPrice(item, cart) {
  const vid = item?.variant_id;
  if (vid && variantSalePriceCache.has(vid)) {
    const cached = variantSalePriceCache.get(vid);
    if (cached > 0) return cached;
  }

  let unit = getBogoUnitPrice(item);
  if (unit > 0) return unit;

  if (cart && vid) {
    const buyLine = cart.items.find(
      i => i.variant_id === vid &&
           !isBogoFreeLine(i) &&
           !(i.selling_plan_allocation && i.selling_plan_allocation.selling_plan)
    );
    if (buyLine) {
      unit = getBogoUnitPrice(buyLine);
      if (unit > 0) return unit;
    }
  }

  if (vid && variantSalePriceCache.has(vid)) {
    return variantSalePriceCache.get(vid) || 0;
  }
  return 0;
}

/**
 * Strikethrough for BOGO FREE — catalog sale price (e.g. $75.99).
 * Compare-at only when higher than sale (true MSRP).
 */
function getBogoFreeStrikeUnitPrice(item, cart) {
  const unit = getBogoCatalogUnitPrice(item, cart);
  let compareAt = variantCompareAtCache.get(item?.variant_id);
  if (typeof compareAt !== 'number' || compareAt <= 0) {
    compareAt = typeof item.compare_at_price === 'number' && item.compare_at_price > 0
      ? item.compare_at_price
      : 0;
  }
  if (compareAt > unit) return compareAt;
  return unit > 0 ? unit : compareAt;
}

function bogoFreeStrikeMeta(item, cart) {
  const unit = getBogoCatalogUnitPrice(item, cart);
  const strike = getBogoFreeStrikeUnitPrice(item, cart);
  return {
    baseUnit: strike > 0 ? strike : null,
    useCompareAtStrike: strike > unit
  };
}

/** When Cart Transform already zeroed one BOGO line, trust cart line prices over position map. */
function getBogoLineRoleFromCartPrices(item, cart) {
  const items = (cart?.items || []).filter(isBogoEligibleItem);
  let unitCount = 0;
  for (const i of items) unitCount += Math.max(0, Number(i.quantity) || 0);
  if (unitCount < 2) return null;

  const lineIsPaid = (i) => {
    const q = Math.max(1, Number(i.quantity) || 1);
    const lineTotal = typeof i.final_line_price === 'number' ? i.final_line_price : i.line_price;
    return typeof lineTotal === 'number' && lineTotal > 0;
  };

  const paid = items.filter(lineIsPaid);
  const free = items.filter(i => !lineIsPaid(i));
  if (paid.length !== 1 || free.length !== 1) return null;
  if (free[0].key === item.key) return 'free';
  if (paid[0].key === item.key) return 'paid';
  return null;
}

function hasPairedBogoFreeLine(items, variantId) {
  return (items || []).some(i => i.variant_id === variantId && isBogoFreeLine(i));
}

/**
 * Single source of truth for B1G1 mix & match (cart line order).
 * eligibleUnitCount includes _bogo_free lines (for pair-complete callout).
 * discountMap excludes split free lines and buy lines already paired with _bogo_free.
 */
function getBogoUnitState(items) {
  if (!BOGO_DRAWER_ENABLED) {
    return { eligibleUnitCount: 0, discountMap: new Map(), isPairComplete: false };
  }
  const list = items || [];
  let eligibleUnitCount = 0;
  for (const item of list) {
    if (!isBogoEligibleItem(item)) continue;
    eligibleUnitCount += Math.max(0, Number(item.quantity) || 0);
  }

  const discountMap = new Map();
  const units = [];
  for (const item of list) {
    if (!isBogoEligibleItem(item)) continue;
    if (isBogoFreeLine(item)) continue;
    if (hasPairedBogoFreeLine(list, item.variant_id)) continue;

    const qty = Math.max(0, Number(item.quantity) || 0);
    for (let i = 0; i < qty; i++) {
      units.push({ key: item.key, price: getBogoUnitPrice(item) });
    }
  }
  for (let i = 0; i < units.length; i++) {
    if (i % 2 === 1) {
      const { key, price } = units[i];
      discountMap.set(key, (discountMap.get(key) || 0) + price);
    }
  }

  return {
    eligibleUnitCount,
    discountMap,
    isPairComplete: eligibleUnitCount >= 2
  };
}

function computeBogoDiscounts(items) {
  return getBogoUnitState(items).discountMap;
}

/**
 * Splits same-variant BOGO buy lines with qty ≥ 2 into:
 *   - a buy line (qty reduced by the free count)
 *   - a separate _bogo_free line (explicit qty of free units)
 *
 * This gives Cart Transform a clean signal: price the free line at $0 and
 * leave the buy line at full price, avoiding the averaging problem.
 * Cross-variant pairs (each qty = 1) are handled by the existing applyBogo
 * path in Cart Transform and shown in the drawer via computeBogoDiscounts.
 *
 * Orphaned _bogo_free lines (buy line removed by customer) are cleaned up.
 */
async function reconcileBogoFreeLines(cartObj) {
  if (!BOGO_DRAWER_ENABLED && !(cartObj?.items || []).some(isBogoFreeLine)) {
    return cartObj;
  }
  if (__RECONCILING_BOGO__) return cartObj;
  __RECONCILING_BOGO__ = true;
  try {
    let cart = cartObj || await fetch('/cart.js').then(r => r.json());
    if (!cartNeedsBogoReconcile(cart)) return cart;

    let cartChanged = false;

    // BOGO off: merge split free qty back into the buy line, then remove _bogo_free rows.
    if (!BOGO_DRAWER_ENABLED) {
      for (const item of [...cart.items]) {
        if (!isBogoFreeLine(item)) continue;
        const buyLine = cart.items.find(
          i => i.variant_id === item.variant_id &&
               !isBogoFreeLine(i) &&
               !(i.selling_plan_allocation && i.selling_plan_allocation.selling_plan)
        );
        if (buyLine) {
          await changeCartLineQuantity(
            buyLine.key,
            (buyLine.quantity || 0) + (item.quantity || 0)
          );
        }
        await changeCartLineQuantity(item.key, 0);
        cartChanged = true;
      }
    } else {
      // 1. Remove _bogo_free lines that are orphaned or no longer eligible.
      for (const item of cart.items) {
        if (!isBogoFreeLine(item)) continue;
        const variantStillEligible = BOGO_FREE_VARIANT_IDS.includes(item.variant_id);
        const hasBuyLine = cart.items.some(
          i => i.variant_id === item.variant_id &&
               !isBogoFreeLine(i) &&
               !(i.selling_plan_allocation && i.selling_plan_allocation.selling_plan)
        );
        if (!variantStillEligible || !hasBuyLine) {
          await changeCartLineQuantity(item.key, 0);
          cartChanged = true;
        }
      }
    }

    if (cartChanged) {
      cart = await fetch('/cart.js').then(r => r.json());
      cartChanged = false;
    }

    if (!BOGO_DRAWER_ENABLED) {
      if (cartChanged) cart = await fetch('/cart.js').then(r => r.json());
      return cart;
    }

    // 2. Split same-variant buy lines with qty ≥ 2.
    for (const item of cart.items) {
      if (!BOGO_FREE_VARIANT_IDS.includes(item.variant_id)) continue;
      if (isBogoFreeLine(item)) continue;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) continue;

      const existingFree = cart.items.find(
        i => i.variant_id === item.variant_id && isBogoFreeLine(i)
      );
      const currentFree = existingFree ? existingFree.quantity : 0;
      // Total logical units = buy qty already in cart + existing free qty
      const totalQty = item.quantity + currentFree;
      const targetFree = Math.floor(totalQty / 2); // B1G1: every 2nd unit is free

      if (targetFree === currentFree) continue; // already in sync

      // Adjust the buy line so that buyQty + targetFree = totalQty
      await changeCartLineQuantity(item.key, totalQty - targetFree);
      cartChanged = true;

      if (targetFree === 0) {
        if (existingFree) {
          await changeCartLineQuantity(existingFree.key, 0);
        }
      } else if (existingFree) {
        await changeCartLineQuantity(existingFree.key, targetFree);
      } else {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.variant_id,
            quantity: targetFree,
            properties: { _bogo_free: 'true' }
          })
        }).catch(() => {});
      }
    }

    if (cartChanged) cart = await fetch('/cart.js').then(r => r.json());
    return cart;
  } finally {
    __RECONCILING_BOGO__ = false;
  }
}

/** True when BOGO free lines need reconciliation (split or orphan cleanup). */
function cartNeedsBogoReconcile(cart) {
  const items = cart?.items || [];

  // When BOGO is off, only clean up leftover _bogo_free split lines from a prior session.
  if (!BOGO_DRAWER_ENABLED) return items.some(i => isBogoFreeLine(i));

  if (items.some(i => isBogoFreeLine(i))) return true;

  for (const item of items) {
    if (!BOGO_FREE_VARIANT_IDS.includes(item.variant_id)) continue;
    if (isBogoFreeLine(item)) continue;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) continue;

    const existingFree = items.find(
      i => i.variant_id === item.variant_id && isBogoFreeLine(i)
    );
    const currentFree = existingFree ? existingFree.quantity : 0;
    const totalQty = (item.quantity || 0) + currentFree;
    const targetFree = Math.floor(totalQty / 2);
    if (targetFree !== currentFree) return true;
  }
  return false;
}

function showCartItemsLoading() {
  const el = document.getElementById('cart-items');
  if (!el) return;
  el.innerHTML = '<div class="cart-items-loading" aria-live="polite">Updating cart…</div>';
  const footer = document.getElementById('cart-footer');
  if (footer) footer.style.display = 'none';
}

// ============================ Utilities =========================
function decodeHTMLEntities(str) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}
function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[ch]);
}
function formatMoney(cents, currencyCode = null) {
  // Try to get currency from cart if not provided - prioritize cart currency over theme
  if (!currencyCode && window.__latestCart) {
    if (window.__latestCart.currency) {
      // Handle both string format (e.g., "USD") and object format (e.g., {iso_code: "USD"})
      currencyCode = typeof window.__latestCart.currency === 'string' 
        ? window.__latestCart.currency 
        : (window.__latestCart.currency.iso_code || null);
    }
  }
  
  // Use Intl.NumberFormat with currency from cart (most reliable for multi-currency)
  if (currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      // If currency code is invalid, fall through to theme or default
    }
  }
  
  // Fallback to theme's formatMoney if available
  if (typeof theme !== 'undefined' && theme && typeof theme.Shopify !== 'undefined' && typeof theme.Shopify.formatMoney === 'function') {
    const format = theme.money_format_with_code_preference || theme.money_format;
    if (format) {
      return theme.Shopify.formatMoney(cents, format);
    }
  }
  
  // Final fallback to USD
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function extractPercentFromText(text) {
  const m = String(text || '').match(/(\d+)\s*%/);
  return m ? parseInt(m[1], 10) : null;
}
function extractMaxPercentFromPlans(plans) {
  let maxP = 0;
  for (const p of plans) {
    const n = extractPercentFromText(p?.name);
    if (n) maxP = Math.max(maxP, n);
  }
  return maxP || SUBSCRIBE_SAVE_FALLBACK_PERCENT;
}

// ---- Cart count badge helper (supports <cart-count> custom element) ----
function updateHeaderCartCount(count) {
  const n = Math.max(0, Number(count || 0));

  const legacy = document.getElementById('cart-count-badge');
  if (legacy) {
    legacy.textContent = String(n);
    legacy.classList.toggle('is-hidden', !(n > 0));
    legacy.setAttribute('aria-hidden', n > 0 ? 'false' : 'true');
  }

  const nodes = document.querySelectorAll('cart-count');
  nodes.forEach((el) => {
    el.textContent = String(n);
    el.setAttribute('aria-label', n === 1 ? '1 item' : `${n} items`);
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('data-count', String(n));
    if (n > 0) {
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
    try { el.dispatchEvent(new CustomEvent('cart-count:updated', { detail: { count: n } })); } catch (_) {}
  });

  const icon = document.querySelector('.header__icon--cart, .cart-drawer-button');
  if (icon) icon.setAttribute('data-count', String(n));
}

// ============================ Discount code helpers ============================
function applyDiscountCode(code) {
  if (!code) return;
  if (REOPEN_DRAWER_AFTER_REDIRECT) sessionStorage.setItem('reopenDrawer', '1');
  sessionStorage.setItem('activeDiscountCode', code);
  sessionStorage.setItem('discountRedirecting', '1');
  window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
}
function clearDiscountCode() {
  if (REOPEN_DRAWER_AFTER_REDIRECT) sessionStorage.setItem('reopenDrawer', '1');
  sessionStorage.removeItem('activeDiscountCode');
  sessionStorage.setItem('discountRedirecting', '1');
  window.location.href = `/cart?discount=`;
}

// ============================ Parse thresholds ============================
function parseThreshold(thresholdStr, currencyCode = null) {
  if (!thresholdStr) return 0;
  
  // If threshold contains ':', it's multi-currency format (e.g., "USD:50000,EUR:45000")
  if (thresholdStr.includes(':')) {
    if (!currencyCode && window.__latestCart) {
      // Get currency from cart if not provided
      const cartCurrency = window.__latestCart.currency;
      currencyCode = typeof cartCurrency === 'string' 
        ? cartCurrency 
        : (cartCurrency?.iso_code || null);
    }
    
    if (currencyCode) {
      const pairs = thresholdStr.split(',');
      for (const pair of pairs) {
        const parts = pair.split(':');
        if (parts.length === 2 && parts[0].trim().toUpperCase() === currencyCode.toUpperCase()) {
          return parseInt(parts[1].trim(), 10) || 0;
        }
      }
    }
    // If currency not found in multi-currency format, return 0
    return 0;
  }
  
  // Single currency format - just parse the number
  return parseInt(thresholdStr.trim(), 10) || 0;
}

// Parse progress data - store raw threshold, parse dynamically when currency is available
const progressData = Array.from(document.querySelectorAll('.progress-bar-thresolds > div'))
  .filter(el => el.className.includes('progress-bar-'))
  .map(el => {
    const cls = el.className;
    const type =
      cls.includes('progress-bar-product')  ? 'product'  :
      cls.includes('progress-bar-discount') ? 'discount' :
      cls.includes('progress-bar-shipping') ? 'shipping' : 'other';
    
    const thresholdRaw = el.dataset.threshold || '0';
    
    // For initial parsing, if it's multi-currency format, use 0 as fallback
    // The actual threshold will be parsed dynamically via getCurrentThreshold()
    const initialThreshold = thresholdRaw.includes(':') ? 0 : (parseInt(thresholdRaw.trim(), 10) || 0);
    
    return {
      type,
      threshold: initialThreshold, // Initial fallback, will be recalculated dynamically
      thresholdRaw: thresholdRaw, // Store raw for dynamic parsing based on current currency
      message: el.dataset.message || '',
      label: el.dataset.label || '',
      img: decodeHTMLEntities(el.dataset.img || ''),
      productId: el.dataset.productId ? parseInt(el.dataset.productId, 10) : null,
      productHandle: el.dataset.productHandle || '',
      variantId: el.dataset.varid || null,
      discountCode: el.dataset.discountCode || '',
      percent: parseInt(el.dataset.percent || '0', 10) || 0
    };
  });

// Helper to get current threshold for an item based on current cart currency
function getCurrentThreshold(item) {
  if (!item.thresholdRaw) return item.threshold || 0;
  
  let currencyCode = null;
  if (window.__latestCart && window.__latestCart.currency) {
    currencyCode = typeof window.__latestCart.currency === 'string' 
      ? window.__latestCart.currency 
      : (window.__latestCart.currency.iso_code || null);
  }
  
  return parseThreshold(item.thresholdRaw, currencyCode);
}

function getDiscountTiers() {
  return progressData
    .filter(p => p.type === 'discount')
    .map(p => ({ ...p, threshold: getCurrentThreshold(p) }))
    .sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
}
function getActiveDiscountTier(cartTotal) {
  const tiers = getDiscountTiers();
  let active = null;
  for (const t of tiers) {
    if (cartTotal >= (t.threshold || 0)) active = t; else break;
  }
  return active;
}

// ============================ Drawer open/close ============================
function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.classList.remove('hidden');
  drawer.classList.add('open');
  // Re-render cached cart only when BOGO split is already in sync (avoids qty-2 flash).
  if (window.__latestCart) {
    if (cartNeedsBogoReconcile(window.__latestCart)) {
      showCartItemsLoading();
    } else {
      updateCartUI(window.__latestCart);
    }
  }
  fetchCart().catch(reportDrawerError);
}
function closeCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.classList.remove('open');
  drawer.classList.add('hidden');
}

// ============================ Discount labels per line ============================
function getDiscountMessages(item, cart) {
  const messages = [];
  const labelFrom = (obj = {}) => {
    const code = obj.code || obj.discount_code || '';
    const title = obj.title || '';
    const description = obj.description || '';
    const parts = [];
    if (code) parts.push(code.toString().toUpperCase());
    if (title && (!code || title.toString().toUpperCase() !== code.toString().toUpperCase())) parts.push(title);
    if (description) parts.push(description);
    return parts.join(' – ');
  };

  if (Array.isArray(item.applied_discounts)) item.applied_discounts.forEach(d => { const m = labelFrom(d); if (m) messages.push(m); });
  if (Array.isArray(item.discounts)) item.discounts.forEach(d => { const m = labelFrom(d); if (m) messages.push(m); });
  if (Array.isArray(item.line_level_discount_allocations)) {
    item.line_level_discount_allocations.forEach(alloc => {
      const app = alloc && (alloc.discount_application || alloc.application || {});
      const m = labelFrom(app); if (m) messages.push(m);
    });
  }
  if (!messages.length && cart && Array.isArray(cart.cart_level_discount_applications)) {
    if (item.line_price < (item.original_line_price || item.line_price)) {
      cart.cart_level_discount_applications.forEach(app => { const m = labelFrom(app); if (m) messages.push(m); });
    }
  }
  return Array.from(new Set(messages)).filter(Boolean);
}

// ============================ Subscription helpers ============================
// Cache product plans to avoid repeated fetches
const productPlanCache = Object.create(null);
const productJsonCache = Object.create(null);

function fetchProductJson(handle) {
  if (!handle) return Promise.resolve(null);
  if (productJsonCache[handle]) return Promise.resolve(productJsonCache[handle]);
  return fetch(`/products/${encodeURIComponent(handle)}.js`)
    .then(r => r.json())
    .then(p => (productJsonCache[handle] = p))
    .catch(() => (productJsonCache[handle] = null));
}

function fetchProductPlans(handle) {
  if (!handle) return Promise.resolve([]);
  if (productPlanCache[handle]) return Promise.resolve(productPlanCache[handle]);
  return fetchProductJson(handle)
    .then(p => {
      if (!p) {
        productPlanCache[handle] = [];
        return [];
      }
      const plans = [];
      (p.selling_plan_groups || []).forEach(g => {
        (g.selling_plans || []).forEach(sp => plans.push({ id: String(sp.id), name: sp.name }));
      });
      productPlanCache[handle] = plans;
      return plans;
    });
}
function addItemToCart(variantId, quantity = 1, sellingPlanId = null, properties = null) {
  const payload = { id: Number(variantId), quantity: Number(quantity) };
  if (sellingPlanId) payload.selling_plan = String(sellingPlanId);
  if (properties && typeof properties === 'object') payload.properties = properties;
  return fetch('/cart/add.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
  }).then(res => res.json());
}
async function convertLineSubscription({ lineKey, variantId, quantity, sellingPlanId }) {
  await fetch('/cart/change.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 0 })
  });

  await addItemToCart(variantId, quantity, sellingPlanId || null);

  if (sellingPlanId) {
    await ensureWelcomeKitPresentIfSubscribing();
  } else {
    await maybeRemoveWelcomeKitIfNoSubscriptions();
  }

  fetchCart(true);
}

// ============================ Price helpers ============================
/** Drawer BOGO off: match /cart.js line totals (ignore Cart Transform compare-at / averaged BOGO). */
function computeNeutralCartLinePrices(item, qty) {
  const lineTotal = (() => {
    if (typeof item.final_line_price === 'number') return item.final_line_price;
    if (typeof item.line_price === 'number') return item.line_price;
    return 0;
  })();

  if (lineTotal <= 0) {
    return { isSubscribed: false, baseUnit: null, subUnit: 0, percent: 0, lineDisplayTotal: 0 };
  }

  const unit = Math.round(lineTotal / qty);
  return {
    isSubscribed: false,
    baseUnit: unit,
    subUnit: unit,
    percent: 0,
    lineDisplayTotal: lineTotal
  };
}

// ---- Promo-rule discount preview (Cart Transform) ----
// Cart Transform discounts don't appear in /cart.js, so we mirror the active
// promo rules (same #promo-storefront-rules data the storefront overlay reads)
// to preview the discounted price + badge in the drawer. Display-only; the real
// discount is applied by the Cart Transform at checkout.
let __promoRuleMapCache = null;
function getPromoRuleMap() {
  if (__promoRuleMapCache) return __promoRuleMapCache;
  const map = new Map();
  try {
    const el = document.getElementById('promo-storefront-rules');
    if (el) {
      const raw = JSON.parse(el.textContent || '{}');
      const doc = typeof raw === 'string' ? JSON.parse(raw) : raw;
      (Array.isArray(doc.rules) ? doc.rules : [doc])
        .filter(rule => {
          const c = (rule && rule.conditions) || {};
          return rule && rule.status !== 'DRAFT' &&
            ['PERCENTAGE', 'FIXED_AMOUNT'].includes(rule.reward && rule.reward.type) &&
            Array.isArray(c.variants_any) && c.variants_any.length &&
            !c.quantity_min && !c.subtotal_min &&
            !(c.products_any && c.products_any.length) &&
            !(c.collections_any && c.collections_any.length) &&
            !(c.customer_tags_any && c.customer_tags_any.length) &&
            !(c.markets_any && c.markets_any.length) &&
            rule.purchase_target !== 'SUBSCRIPTION';
        })
        .forEach(rule => {
          rule.conditions.variants_any.forEach(gid => {
            const id = String(gid).split('/').pop();
            if (!map.has(id)) map.set(id, rule);
          });
        });
    }
  } catch (e) { /* ignore malformed rules */ }
  __promoRuleMapCache = map;
  return map;
}

function getPromoRuleForItem(item) {
  if (!item) return null;
  return getPromoRuleMap().get(String(item.variant_id)) || null;
}

function getCatalogSaleMetaForPromo(item, unitOriginal) {
  const compareAt = variantCompareAtCache.get(item?.variant_id);
  if (!(compareAt > unitOriginal)) return null;
  if (!getPromoRuleForItem(item)) return null;
  const percent = Math.round((1 - (unitOriginal / compareAt)) * 100);
  return {
    baseUnit: compareAt,
    subUnit: unitOriginal,
    percent: Math.max(0, percent),
    isPromoLine: true,
    isCatalogSalePromo: true,
    useCompareAtStrike: true
  };
}

// Discounted unit price (cents) for a promo-matched one-time line, or null.
// The Cart Transform already discounts the /cart.js line, so `unitOriginal` (the
// line's cart price) IS the charged, discounted price. Reconstruct the
// pre-discount unit price from the rule so we can show a correct single
// strikethrough — do NOT apply the discount a second time.
function getPromoTransformMeta(item, unitOriginal) {
  if (!item || !(unitOriginal > 0)) return null;
  if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) return null;
  const rule = getPromoRuleForItem(item);
  if (!rule) return null;
  const reward = rule.reward || {};
  let original = null;
  if (reward.type === 'PERCENTAGE') {
    const pct = parseFloat(reward.percentage);
    if (!isFinite(pct) || pct <= 0 || pct >= 100) return null;
    original = Math.round(unitOriginal / (1 - pct / 100));
  } else if (reward.type === 'FIXED_AMOUNT') {
    const amt = Math.round(parseFloat(reward.fixed_amount) * 100);
    if (!isFinite(amt) || amt <= 0) return null;
    original = unitOriginal + amt;
  }
  if (!original || original <= unitOriginal) return null;
  return {
    isSubscribed: false,
    baseUnit: original,
    subUnit: unitOriginal,
    percent: Math.round((1 - (unitOriginal / original)) * 100),
    isPromoLine: true
  };
}

// For subscribed lines: reverse-calc strike-through from subscription %
// For one-time lines: show original vs discounted (incl. cart-level codes).
// For threshold freebies/welcome kit: show FREE only.
// For $0 BOGO lines (Cart Transform): show BOGO FREE badge + struck-through original price.
function computeSubscriptionPrices(item, cart) {
  const isSubscribed = !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  const qty = Math.max(1, item.quantity || 1);

  const welcomeKitConfig = getWelcomeKitConfig();
  const isWelcomeKit = welcomeKitConfig.enabled && item.variant_id === welcomeKitConfig.variantId;
  const isThresholdFree = isFreebieLine(item);

  if (!BOGO_DRAWER_ENABLED) {
    if (isWelcomeKit || isThresholdFree) {
      const freeLabel = isThresholdFree ? 'BOGO GIFT' : 'FREE';
      return { isSubscribed: false, baseUnit: null, subUnit: 0, percent: 0, isZeroFree: true, freeLabel };
    }
    if (!isSubscribed) {
      const unitOriginal = (() => {
        if (typeof item.original_price === 'number' && item.original_price > 0) return item.original_price;
        if (typeof item.price === 'number' && item.price > 0) return item.price;
        if (typeof item.original_line_price === 'number' && item.original_line_price > 0) {
          return Math.round(item.original_line_price / qty);
        }
        return typeof item.price === 'number' ? item.price
          : (typeof item.final_price === 'number' ? item.final_price
            : Math.round((item.line_price || 0) / qty));
      })();
      let unitDiscounted = null;
      if (typeof item.final_line_price === 'number' && item.final_line_price > 0) {
        unitDiscounted = Math.round(item.final_line_price / qty);
      } else if (
        typeof item.line_price === 'number' &&
        typeof item.original_line_price === 'number' &&
        item.line_price > 0 &&
        item.original_line_price > 0 &&
        item.line_price < item.original_line_price
      ) {
        unitDiscounted = Math.round(item.line_price / qty);
      }
      if (unitDiscounted === null && cart) {
        const sub = Number(cart.items_subtotal_price || 0);
        const tot = Number(cart.total_price || 0);
        const haveCartLevel = Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
        if (haveCartLevel && sub > 0 && tot >= 0 && tot < sub) {
          unitDiscounted = Math.round(unitOriginal * (tot / sub));
        }
      }
      // The Cart Transform already discounted this line in /cart.js. Show the
      // strikethrough by reconstructing the pre-discount price — never re-apply
      // the discount (that caused the double-discount bug).
      let isPromoLine = false;
      if (unitDiscounted === null || unitDiscounted >= unitOriginal) {
        const catalogSaleMeta = getCatalogSaleMetaForPromo(item, unitOriginal);
        if (catalogSaleMeta) return catalogSaleMeta;
        const promoMeta = getPromoTransformMeta(item, unitOriginal);
        if (promoMeta) return promoMeta;
      }
      let percent = 0;
      if (unitDiscounted !== null && unitDiscounted < unitOriginal) {
        percent = Math.round((1 - (unitDiscounted / unitOriginal)) * 100);
      }
      const isZeroByFunction =
        [item.price, item.final_price, item.line_price, item.final_line_price]
          .some(v => typeof v === 'number' && v === 0);
      if (isZeroByFunction) {
        return {
          isSubscribed: false,
          baseUnit: unitOriginal > 0 ? unitOriginal : null,
          subUnit: 0,
          percent: 0,
          isZeroFree: true,
          freeLabel: 'FREE'
        };
      }
      return { isSubscribed: false, baseUnit: unitOriginal, subUnit: unitDiscounted, percent, isPromoLine };
    }
    const percentMatch = String(item.selling_plan_allocation?.selling_plan?.name || '').match(/(\d+)\s*%/);
    const percent = percentMatch ? parseInt(percentMatch[1], 10) : SUBSCRIBE_SAVE_FALLBACK_PERCENT;
    const discountedUnit = (typeof item.price === 'number') ? item.price
      : (typeof item.final_price === 'number') ? item.final_price
      : Math.round((item.line_price || 0) / qty);
    const denom = Math.max(0.01, (1 - percent / 100));
    const originalUnit = Math.round(discountedUnit / denom);
    return { isSubscribed: true, baseUnit: originalUnit, subUnit: discountedUnit, percent };
  }

  const bogoState = window.__bogoState;
  const isPairComplete = Boolean(bogoState?.isPairComplete);
  const isBogoVariant = BOGO_FREE_VARIANT_IDS.includes(item.variant_id);

  // Explicit BOGO split-line — badge + variant price struck through
  if (isBogoFreeLine(item)) {
    const { baseUnit, useCompareAtStrike } = bogoFreeStrikeMeta(item, cart);
    return {
      isSubscribed: false, baseUnit, subUnit: 0, percent: 100,
      isZeroFree: true, freeLabel: BOGO_FREE_BADGE_LABEL, isBogoFreeBadge: true,
      useCompareAtStrike
    };
  }

  const transformRole = isBogoVariant && isPairComplete
    ? getBogoLineRoleFromCartPrices(item, cart)
    : null;

  if (transformRole === 'free') {
    const { baseUnit, useCompareAtStrike } = bogoFreeStrikeMeta(item, cart);
    return {
      isSubscribed: false,
      baseUnit: baseUnit > 0 ? baseUnit : null,
      subUnit: 0,
      percent: 100,
      isZeroFree: true,
      freeLabel: BOGO_FREE_BADGE_LABEL,
      isBogoFreeBadge: true,
      useCompareAtStrike
    };
  }

  if (transformRole === 'paid') {
    const unit = getBogoUnitPrice(item);
    return {
      isSubscribed: false,
      baseUnit: unit,
      subUnit: unit,
      percent: 0
    };
  }

  const bogoDiscount = window.__bogoDiscounts.get(item.key);
  const isBogoFreeByMap = bogoDiscount != null && bogoDiscount > 0;

  // BOGO map before isZeroByFunction so cross-variant pairs label the correct line
  if (isBogoFreeByMap) {
    const originalUnit = getBogoUnitPrice(item);
    const lineTotal = originalUnit * qty;
    const discountedUnit = Math.round(Math.max(0, lineTotal - bogoDiscount) / qty);
    const percent = originalUnit > 0
      ? Math.round((1 - discountedUnit / originalUnit) * 100)
      : 100;
    const isFullyFree = discountedUnit === 0;
    const strikeMeta = isFullyFree ? bogoFreeStrikeMeta(item, cart) : { baseUnit: originalUnit, useCompareAtStrike: false };
    return {
      isSubscribed: false,
      baseUnit: isFullyFree ? strikeMeta.baseUnit : originalUnit,
      subUnit: discountedUnit,
      percent,
      isBogoItem: true,
      isZeroFree: isFullyFree,
      freeLabel: isFullyFree ? BOGO_FREE_BADGE_LABEL : null,
      isBogoFreeBadge: isFullyFree,
      useCompareAtStrike: isFullyFree ? strikeMeta.useCompareAtStrike : false
    };
  }

  const isZeroByFunction =
    [item.price, item.final_price, item.line_price, item.final_line_price]
      .some(v => typeof v === 'number' && v === 0);

  if (isWelcomeKit || isThresholdFree) {
    const freeLabel = isThresholdFree ? 'BOGO GIFT' : 'FREE';
    return { isSubscribed: false, baseUnit: null, subUnit: 0, percent: 0, isZeroFree: true, freeLabel };
  }

  // Cart Transform $0 on wrong line in a complete cross-variant pair — show paid price via normal path
  const skipZeroForPaidBogoLine =
    isZeroByFunction &&
    isBogoVariant &&
    isPairComplete &&
    !isBogoFreeByMap;

  if (isZeroByFunction && !skipZeroForPaidBogoLine) {
    const showBogoFree = isBogoVariant && isPairComplete;
    const strikeMeta = showBogoFree
      ? bogoFreeStrikeMeta(item, cart)
      : { baseUnit: getBogoUnitPrice(item) || null, useCompareAtStrike: false };
    return {
      isSubscribed: false,
      baseUnit: strikeMeta.baseUnit > 0 ? strikeMeta.baseUnit : null,
      subUnit: 0,
      percent: 0,
      isZeroFree: true,
      freeLabel: showBogoFree ? BOGO_FREE_BADGE_LABEL : 'FREE',
      isBogoFreeBadge: showBogoFree,
      useCompareAtStrike: strikeMeta.useCompareAtStrike
    };
  }

  if (!isSubscribed) {
    const unitOriginal = (() => {
      const fromOriginal = getBogoUnitPrice(item);
      if (fromOriginal > 0) return fromOriginal;
      if (typeof item.original_line_price === 'number' && item.original_line_price > 0) {
        return Math.round(item.original_line_price / qty);
      }
      return typeof item.price === 'number' ? item.price
        : (typeof item.final_price === 'number' ? item.final_price
          : Math.round((item.line_price || 0) / qty));
    })();

    let unitDiscounted = null;

    if (typeof item.final_line_price === 'number' && item.final_line_price > 0) {
      unitDiscounted = Math.round(item.final_line_price / qty);
    } else if (
      typeof item.line_price === 'number' &&
      typeof item.original_line_price === 'number' &&
      item.line_price > 0 &&
      item.original_line_price > 0 &&
      item.line_price < item.original_line_price
    ) {
      unitDiscounted = Math.round(item.line_price / qty);
    }

    if (unitDiscounted === null && cart) {
      const sub = Number(cart.items_subtotal_price || 0);
      const tot = Number(cart.total_price || 0);
      const haveCartLevel = Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
      if (haveCartLevel && sub > 0 && tot >= 0 && tot < sub) {
        const factor = tot / sub; // e.g., 0.90 when 10% cart-level discount
        unitDiscounted = Math.round(unitOriginal * factor);
      }
    }

    let percent = 0;
    if (unitDiscounted !== null && unitDiscounted < unitOriginal) {
      percent = Math.round((1 - (unitDiscounted / unitOriginal)) * 100);
    }

    return {
      isSubscribed: false,
      baseUnit: unitOriginal,
      subUnit: unitDiscounted,
      percent
    };
  }

  const percentMatch = String(item.selling_plan_allocation?.selling_plan?.name || '').match(/(\d+)\s*%/);
  const percent = percentMatch ? parseInt(percentMatch[1], 10) : SUBSCRIBE_SAVE_FALLBACK_PERCENT;

  const discountedUnit = (typeof item.price === 'number') ? item.price
                        : (typeof item.final_price === 'number') ? item.final_price
                        : Math.round((item.line_price || 0) / qty);

  const denom = Math.max(0.01, (1 - percent / 100));
  const originalUnit = Math.round(discountedUnit / denom);

  return {
    isSubscribed: true,
    baseUnit: originalUnit,
    subUnit: discountedUnit,
    percent
  };
}

function buildBogoOrFreePriceHtml(label, isBogoFreeBadge, baseTotal, useCompareAtStrike) {
  const safeLabel = escapeHtml(label || 'FREE');
  const badgeHtml = isBogoFreeBadge
    ? `<span class="bogo-free-badge">${safeLabel}</span>`
    : `<span class="free-price">${safeLabel}</span>`;
  if (baseTotal != null && baseTotal > 0) {
    const strikeClass = useCompareAtStrike ? 'original-price compare-at-price' : 'original-price';
    return `${badgeHtml} <span class="${strikeClass}">${formatMoney(baseTotal)}</span>`;
  }
  return badgeHtml;
}

// ==== Line-price (qty-aware) renderer ====
// Uses computeSubscriptionPrices (unit prices) and multiplies by item.quantity
function buildLinePriceHTML(item, cart) {
  const qty = Math.max(1, item.quantity || 1);
  const meta = computeSubscriptionPrices(item, cart);
  const { baseUnit, subUnit, percent, isZeroFree, freeLabel, isBogoItem, isBogoFreeBadge, useCompareAtStrike, lineDisplayTotal } = meta;

  if (lineDisplayTotal != null && lineDisplayTotal > 0) {
    return { html: `<span>${formatMoney(lineDisplayTotal)}</span>`, percent: 0 };
  }

  if (isZeroFree) {
    const label = freeLabel || 'FREE';
    const baseTotal = (baseUnit != null && baseUnit > 0) ? baseUnit * qty : null;
    return {
      html: buildBogoOrFreePriceHtml(label, isBogoFreeBadge, baseTotal, useCompareAtStrike),
      percent: 0
    };
  }

  const baseTotal = (baseUnit != null) ? baseUnit * qty : null;
  const subTotal  = (subUnit  != null) ? subUnit  * qty : null;

  if (subTotal != null && baseTotal != null && subTotal < baseTotal) {
    if (subTotal === 0 && isBogoItem) {
      return {
        html: buildBogoOrFreePriceHtml(
          BOGO_FREE_BADGE_LABEL,
          true,
          baseTotal,
          Boolean(meta.useCompareAtStrike)
        ),
        percent: Math.max(0, percent || 0)
      };
    }
    const subDisplay = subTotal === 0
      ? `<span class="free-price">FREE</span>`
      : `<span class="discounted-price">${formatMoney(subTotal)}</span>`;
    const strikeClass = meta.useCompareAtStrike ? 'original-price compare-at-price' : 'original-price';
    const html = `${subDisplay} <span class="${strikeClass}">${formatMoney(baseTotal)}</span>`;
    return { html, percent: Math.max(0, percent || 0), isPromoLine: Boolean(meta.isPromoLine) };
  }

  if (baseTotal != null) {
    return { html: `<span>${formatMoney(baseTotal)}</span>`, percent: 0 };
  }

  return { html: `<span>${formatMoney((item.final_line_price || item.line_price || 0))}</span>`, percent: 0 };
}

// Drawer subtotal aligned with line-level BOGO display (when BOGO items present).
// Promo (Cart Transform) savings in cents — sum of per-line discounts we preview.
function promoSavingsCents(cart) {
  let saved = 0;
  (cart?.items || []).forEach(item => {
    const qty = Math.max(1, item.quantity || 1);
    const meta = computeSubscriptionPrices(item, cart);
    if (!meta.isCatalogSalePromo && meta.isPromoLine && meta.baseUnit != null && meta.subUnit != null && meta.subUnit < meta.baseUnit) {
      saved += (meta.baseUnit - meta.subUnit) * qty;
    }
  });
  return saved;
}

function computeDrawerDisplaySubtotal(cart) {
  // total_price already reflects the Cart Transform discount — don't subtract again.
  if (!BOGO_DRAWER_ENABLED) return Number(cart?.total_price || 0);
  const state = getBogoUnitState(cart?.items || []);
  if (state.eligibleUnitCount === 0) {
    return Number(cart?.total_price || 0);
  }
  let total = 0;
  (cart.items || []).forEach(item => {
    if (isFreebieLine(item)) return;
    const qty = Math.max(1, item.quantity || 1);
    const meta = computeSubscriptionPrices(item, cart);
    if (meta.isZeroFree) return;
    let unit = 0;
    if (meta.subUnit != null && meta.subUnit > 0) unit = meta.subUnit;
    else if (meta.baseUnit != null && meta.baseUnit > 0) unit = meta.baseUnit;
    else {
      const fallback = getBogoUnitPrice(item);
      unit = fallback > 0 ? fallback : Math.round((item.final_line_price || item.line_price || 0) / qty);
    }
    total += unit * qty;
  });
  return total;
}

// ---- Cart-wide savings (in cents). Excludes threshold freebies.
function computeTotalSavings(cart) {
  let saved = 0;
  (cart.items || []).forEach(item => {
    if (item?.properties && (item.properties.free_product === true || item.properties.free_product === 'true')) return;
    if (!BOGO_DRAWER_ENABLED && isBogoFreeLine(item)) return;
    const qty = Math.max(1, item.quantity || 1);
    const { baseUnit, subUnit } = computeSubscriptionPrices(item, cart);
    if (subUnit !== null && baseUnit !== null && baseUnit > subUnit) {
      saved += (baseUnit - subUnit) * qty;
    }
  });
  return Math.max(0, saved);
}

// ============================ Welcome Kit helpers ============================
async function ensureWelcomeKitPresentIfSubscribing(cartObj) {
  const welcomeKitConfig = getWelcomeKitConfig();
  if (!welcomeKitConfig.enabled || !welcomeKitConfig.variantId) return;
  
  try {
    const cart = cartObj || await fetch('/cart.js').then(r => r.json());
    const kitLines = cart.items.filter(i => i.variant_id === welcomeKitConfig.variantId);
    if (kitLines.length === 0) {
      await addItemToCart(welcomeKitConfig.variantId, 1, null, { welcome_kit: true });
    } else {
      if (kitLines.reduce((s, l) => s + (l.quantity || 0), 0) > 1) {
        await enforceWelcomeKitQuantity(cart);
      }
    }
  } catch (e) { /* noop */ }
}
async function maybeRemoveWelcomeKitIfNoSubscriptions(cartObj) {
  const welcomeKitConfig = getWelcomeKitConfig();
  if (!welcomeKitConfig.enabled || !welcomeKitConfig.variantId || !welcomeKitConfig.autoRemove) return;
  
  try {
    const cart = cartObj || await fetch('/cart.js').then(r => r.json());
    const hasSubscription = cart.items.some(i => i.selling_plan_allocation && i.selling_plan_allocation.selling_plan);
    if (!hasSubscription) {
      const kitLines = cart.items.filter(i => i.variant_id === welcomeKitConfig.variantId);
      for (const line of kitLines) {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
      }
    }
  } catch (e) { /* noop */ }
}
async function enforceWelcomeKitQuantity(cartObj) {
  const welcomeKitConfig = getWelcomeKitConfig();
  if (!welcomeKitConfig.enabled || !welcomeKitConfig.variantId) return;
  
  if (__ENFORCING_WELCOME_KIT__) return;
  __ENFORCING_WELCOME_KIT__ = true;
  try {
    const cart = cartObj || await fetch('/cart.js').then(r => r.json());
    const kitLines = cart.items.filter(i => i.variant_id === welcomeKitConfig.variantId);
    const totalQty = kitLines.reduce((s, l) => s + (l.quantity || 0), 0);
    if (totalQty <= 1) return;
    let kept = false;
    for (const line of kitLines) {
      if (!kept) {
        if (line.quantity !== 1) {
          await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 1 })
          });
        }
        kept = true;
      } else {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
      }
    }
  } finally {
    __ENFORCING_WELCOME_KIT__ = false;
  }
}
async function removeWelcomeKitIfCartHasOnlyKit(cartObj) {
  const welcomeKitConfig = getWelcomeKitConfig();
  if (!welcomeKitConfig.enabled || !welcomeKitConfig.variantId) return false;
  
  try {
    const cart = cartObj || await fetch('/cart.js').then(r => r.json());
    const kitLines = cart.items.filter(i => i.variant_id === welcomeKitConfig.variantId);
    const nonKitLines = cart.items.filter(i => i.variant_id !== welcomeKitConfig.variantId);
    if (kitLines.length > 0 && nonKitLines.length === 0) {
      for (const line of kitLines) {
        await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
      }
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

// ============================ Threshold free gift (from progress "free_product" blocks) ============================
function getFreeProductTiers() {
  return progressData
    .filter(p => p.type === 'product' && p.productHandle)
    .map(p => ({
      handle: p.productHandle,
      variantId: p.variantId ? parseInt(p.variantId, 10) : null,
      threshold: getCurrentThreshold(p)
    }));
}

function getCartTotalForFreeGiftThreshold(cart) {
  return Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
}

function isFreebieLine(item) {
  const prop = item.properties;
  return !!(prop && (prop.free_product === true || prop.free_product === 'true'));
}


/** Handles of threshold free gifts the cart currently qualifies for. */
function getEligibleFreeGiftHandles(cart) {
  const cartTotal = getCartTotalForFreeGiftThreshold(cart);
  const tiers = getFreeProductTiers();
  return new Set(
    tiers.filter(t => cartTotal >= (t.threshold || 0)).map(t => t.handle)
  );
}

/** Hide threshold gifts in the drawer until subtotal qualifies (avoids flash before sync removes them). */
function isPhantomBogoCompanionLine() {
  // BOGO drawer feature is disabled — no phantom companion lines to hide.
  return false;
}

function getCartItemsForDisplay(cart) {
  const items = cart?.items || [];
  let visible = items.filter(item => !isPhantomBogoCompanionLine(item, items));

  const eligibleHandles = getEligibleFreeGiftHandles(cart);
  if (!getFreeProductTiers().length) return visible;
  return visible.filter(item => {
    if (!isFreebieLine(item)) return true;
    return eligibleHandles.has(item.handle);
  });
}

function getDrawerVisibleItemCount(cart) {
  return getCartItemsForDisplay(cart).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
}

async function changeCartLineQuantity(lineKey, quantity) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity })
  });
}

function cartThresholdFreebiesNeedsSync(cart) {
  const tiers = getFreeProductTiers();
  if (!tiers.length) return false;
  const items = cart?.items || [];
  const cartTotal = getCartTotalForFreeGiftThreshold(cart);
  const eligibleHandles = new Set(
    tiers.filter(t => cartTotal >= (t.threshold || 0)).map(t => t.handle)
  );

  for (const item of items) {
    if (isFreebieLine(item) && !eligibleHandles.has(item.handle)) return true;
  }

  for (const tier of tiers) {
    if (cartTotal < (tier.threshold || 0)) continue;
    const freebieLines = items.filter(i => i.handle === tier.handle && isFreebieLine(i));
    if (!freebieLines.length) return true;
    if (freebieLines.length > 1) return true;
    if (Number(freebieLines[0].quantity) !== 1) return true;
  }
  return false;
}

/** Add or remove configured free product lines when cart subtotal crosses each tier threshold. */
async function syncThresholdFreebies(cartObj) {
  if (__SYNCING_THRESHOLD_FREEBIES__) return cartObj;
  __SYNCING_THRESHOLD_FREEBIES__ = true;
  try {
    let cart = cartObj || await fetch('/cart.js').then(r => r.json());
    if (!cartThresholdFreebiesNeedsSync(cart)) return cart;

    const cartTotal = getCartTotalForFreeGiftThreshold(cart);
    const tiers = getFreeProductTiers();
    let cartChanged = false;

    const eligibleHandles = new Set(
      tiers.filter(t => cartTotal >= (t.threshold || 0)).map(t => t.handle)
    );

    for (const item of cart.items) {
      if (!isFreebieLine(item)) continue;
      if (eligibleHandles.has(item.handle)) continue;
      await changeCartLineQuantity(item.key, 0);
      cartChanged = true;
    }

    if (cartChanged) cart = await fetch('/cart.js').then(r => r.json());

    for (const tier of tiers) {
      if (cartTotal < (tier.threshold || 0)) continue;

      const freebieLines = cart.items.filter(i => i.handle === tier.handle && isFreebieLine(i));
      if (freebieLines.length) {
        let kept = false;
        for (const line of freebieLines) {
          if (!kept) {
            if (line.quantity !== 1) {
              await changeCartLineQuantity(line.key, 1);
              cartChanged = true;
            }
            kept = true;
          } else {
            await changeCartLineQuantity(line.key, 0);
            cartChanged = true;
          }
        }
        continue;
      }

      let variantId = tier.variantId;
      if (!variantId) {
        const product = await fetchProductJson(tier.handle).catch(() => null);
        const firstAvailable = product?.variants?.find(v => v && v.available);
        variantId = firstAvailable ? parseInt(firstAvailable.id, 10) : null;
      }
      if (!variantId) continue;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { free_product: true } })
      }).catch(() => {});
      cartChanged = true;
    }

    if (cartChanged) cart = await fetch('/cart.js').then(r => r.json());
    return cart;
  } finally {
    __SYNCING_THRESHOLD_FREEBIES__ = false;
  }
}

// ============================ UI Renderers ============================
function updateCartUI(data) {
  if (__RECONCILING_BOGO__ || cartNeedsBogoReconcile(data)) {
    showCartItemsLoading();
    return;
  }

  window.__latestCart = data;
  const displayItems = getCartItemsForDisplay(data);
  window.__bogoState = getBogoUnitState(data.items);
  window.__bogoDiscounts = BOGO_DRAWER_ENABLED ? window.__bogoState.discountMap : new Map();

  const itemsContainer = document.getElementById('cart-items');
  itemsContainer.innerHTML = '';
  if (displayItems.length === 0) {
     itemsContainer.innerHTML = `
    <div class="empty-cart" style="text-align:center; padding:20px 20px; font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
      <p style="font-weight:400; font-size:18px; text-transform:uppercase; margin-bottom:10px; letter-spacing:0.4px; color:#222;">
        Your Cart is Empty
      </p>
      <h2 style="font-weight:800; font-size:22px; line-height:1.3; text-transform:uppercase; margin-bottom:25px; color:#000; text-align:center;">
      TRAIN HARD.<br>RECOVER HARDER.
      </h2>

      <div class="empty-cart-categories" style="
        display:grid;
        grid-template-columns:repeat(2,1fr);
        gap:20px;
        justify-items:center;
        margin-bottom:40px;">
        
        <a href="/collections/protein-powder" class="empty-cat" style="text-decoration:none; color:#000;">
          <img src="https://cdn.shopify.com/s/files/1/0288/7738/9955/files/Generated_Image_October_31_2025_-_6_33PM.png?v=1761917059" 
               alt="Protein Powder" 
               style="width:100%; border-radius:10px; box-shadow:0 2px 6px rgba(0,0,0,0.1);">
          <p style="font-size:13px; margin-top:8px; text-transform:uppercase; letter-spacing: 0.6px;">Protein Powder</p>
        </a>

        <a href="/collections/sports-nutrition-supplements" class="empty-cat" style="text-decoration:none; color:#000;">
          <img src="https://cdn.shopify.com/s/files/1/0288/7738/9955/files/03_2.png?v=1761912365" 
               alt="sports-nutrition-supplements" 
               style="width:100%; border-radius:10px; box-shadow:0 2px 6px rgba(0,0,0,0.1);">
          <p style="font-weight:500; font-size:13px; margin-top:8px; text-transform:uppercase; letter-spacing: 0.6px;">Supplements</p>
        </a>

        <a href="/collections/amino-acids-supplements" class="empty-cat" style="text-decoration:none; color:#000;">
          <img src="https://cdn.shopify.com/s/files/1/0288/7738/9955/files/3_a75d5555-f1b7-4a4a-a320-c67e3a4c53b4.jpg?v=1761912363" 
               alt="Baking" 
               style="width:100%; border-radius:10px; box-shadow:0 2px 6px rgba(0,0,0,0.1);">
          <p style="font-weight:500; font-size:13px; margin-top:8px; text-transform:uppercase; letter-spacing: 0.6px;">Amino Acids</p>
        </a>

        <a href="/collections/health-wellness-supplements" class="empty-cat" style="text-decoration:none; color:#000;">
          <img src="https://cdn.shopify.com/s/files/1/0288/7738/9955/files/Proven_greens_and_red_kitchen_-min.png?v=1761912370" 
               alt="Daily Essentials" 
               style="width:100%; border-radius:10px; box-shadow:0 2px 6px rgba(0,0,0,0.1);">
          <p style="font-weight:500; font-size:13px; margin-top:8px; text-transform:uppercase; letter-spacing: 0.6px;">Wellness Supplement</p>
        </a>
      </div>

      <a href="/collections/all-products" class="button button--primary  button--fixed pointer-events-auto" style="
        display:inline-flex;
        align-items:center;
        justify-content:center;
        background:#000;
        color:#fff;
        font-weight:700;
        text-transform:uppercase;
        font-size:13px;
        padding:14px 28px;
        border-radius:6px;
        text-decoration:none;
        letter-spacing:0.5px;
        transition:background 0.2s ease;">
        Shop All Products
      </a>
    </div>`;

   // Hide footer and in-demand section
  const footer = document.getElementById('cart-footer');
  if (footer) footer.style.display = 'none';
  const inDemand = document.querySelector('.cart-in-demand-main');
  if (inDemand) inDemand.style.display = 'none';
  const cta = document.getElementById('discount-cta'); 
  if (cta) cta.innerHTML = '';
  updateHeaderCartCount(0);
  return;
  }

  const fragment = document.createDocumentFragment();

  displayItems.forEach((item, idx) => {
    const properties = item.properties
      ? Object.entries(item.properties)
          .filter(([k]) => !k.startsWith('_'))
          .map(([k, v]) => `${escapeHtml(k)}: ${escapeHtml(v)}`)
          .join('<br>')
      : '';

    const discountMessages = getDiscountMessages(item, data);
    const welcomeKitConfig = getWelcomeKitConfig();
    const isWelcomeKit = welcomeKitConfig.enabled && item.variant_id === welcomeKitConfig.variantId;
    const isBogoFreeItem = BOGO_DRAWER_ENABLED && isBogoFreeLine(item);
    const showBogoCallout = false;

    const subCtlId = `subctl-${idx}`;
    const isFreebie = isFreebieLine(item);
    const isPromoGift = (item.properties && item.properties[GIFT_PROP] === 'true') || window.PumpkinCampaign?.isManaged(item);
    const hideQuantityControls = isFreebie || isPromoGift;
    const variantCtlId = `freebie-variant-${idx}`;
    const itemEl = document.createElement('div');
    itemEl.className = 'cart-item';
    itemEl.dataset.pumpkinManaged = window.PumpkinCampaign?.isManaged(item) ? 'true' : 'false';

    // Line-total price block
    const { html: priceHtml, percent, isPromoLine } = buildLinePriceHTML(item, data);
    const isSubscribedLine = !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
    // Subscribe & Save % on subscription lines; promo (Cart Transform) % OFF on matching one-time lines.
    const discountBadgeHtml = (percent && percent > 0 && !isBogoFreeItem && (isSubscribedLine || isPromoLine))
      ? `<span class="save-badge">${(isPromoLine && !isSubscribedLine) ? `${percent}% OFF` : `Save ${percent}%`}</span>`
      : '';

    itemEl.innerHTML = `<div class="cart-item-inner" data-productid="${item.id}" data-line-key="${escapeHtml(item.key)}">
      <img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" />
      <div class="cart-details">
        <div class="block leading-tight">
          <a href="${escapeHtml(item.url)}" class="horizontal-product__title reversed-link font-medium text-base leading-tight">
            ${escapeHtml(item.product_title)}
          </a>
        </div>

        ${!isFreebie ? `<div class="cart-price">${priceHtml} ${discountBadgeHtml}</div>` : ''}

        <div class="cart-variant-name">${escapeHtml(item.variant_title === 'Default Title' ? '' : item.variant_title || '')}</div>
        ${isSubscribedLine ? `<div class="cart-selling-plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</div>` : ''}
        ${properties && !isFreebie ? `<div class="cart-properties">${properties}</div>` : ''}
        ${(discountMessages.length || (showBogoCallout && !isFreebie)) ? `<div class="cart-discounts">${discountMessages.map(m => `<div class="discount-msg">${escapeHtml(m)}</div>`).join('')}${showBogoCallout && !isFreebie ? `<div class="discount-msg">Add one more to get 1 Free</div>` : ''}</div>` : ''}
        ${isFreebie ? `<div class="freebie-variant-mount" id="${variantCtlId}"></div>` : ''}
        <div class="cart-mobile-view">
          ${!hideQuantityControls ? `<div class="custom-cart-quantity">
            <button class="qty-btn" type="button" aria-label="Decrease quantity for ${escapeHtml(item.product_title)}" data-key="${escapeHtml(item.key)}" data-action="decrease"><svg role="presentation" focusable="false" width="10" height="2" class="icon icon-minus" viewBox="0 0 10 2"><path d="M0 0H10V2H0V0Z" fill="currentColor"></path></svg></button>
            <span class="cart-quantity">${item.quantity}</span>
            <button class="qty-btn" type="button" aria-label="Increase quantity for ${escapeHtml(item.product_title)}" data-key="${escapeHtml(item.key)}" data-action="increase"><svg role="presentation" focusable="false" stroke-width="2" width="10" height="10" class="icon icon-plus" viewBox="0 0 12 12"><path d="M6 0V12" fill="none" stroke="currentColor"></path><path d="M0 6L12 6" fill="none" stroke="currentColor"></path></svg></button>
          </div>` : ''}
          <button type="button" class="cart-remove" onclick="removeCartItem('${escapeHtml(item.key)}')" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
        </div>
      </div>

      <div class="cart-actions">
        ${!hideQuantityControls ? `<div class="custom-cart-quantity">
          <button class="qty-btn" type="button" aria-label="Decrease quantity for ${escapeHtml(item.product_title)}" data-key="${escapeHtml(item.key)}" data-action="decrease"><svg role="presentation" focusable="false" width="10" height="2" class="icon icon-minus" viewBox="0 0 10 2"><path d="M0 0H10V2H0V0Z" fill="currentColor"></path></svg></button>
          <span class="cart-quantity">${item.quantity}</span>
          <button class="qty-btn" type="button" aria-label="Increase quantity for ${escapeHtml(item.product_title)}" data-key="${escapeHtml(item.key)}" data-action="increase"><svg role="presentation" focusable="false" stroke-width="2" width="10" height="10" class="icon icon-plus" viewBox="0 0 12 12"><path d="M6 0V12" fill="none" stroke="currentColor"></path><path d="M0 6L12 6" fill="none" stroke="currentColor"></path></svg></button>
        </div>` : ''}
        <button type="button" class="cart-remove" onclick="removeCartItem('${escapeHtml(item.key)}')" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
      </div>
    </div>

    <!-- Subscription control mounts here -->
    <div class="subscription-mount" id="${subCtlId}"></div>`;

    fragment.appendChild(itemEl);

    if (isFreebie) {
      const freebieMount = itemEl.querySelector(`#${variantCtlId}`);
      if (freebieMount) hydrateFreebieVariantSelector(freebieMount, item);
    }

    // Lock qty/remove only for active BOGO split lines
    if (BOGO_DRAWER_ENABLED && isBogoFreeItem) {
      itemEl.querySelectorAll('.qty-btn').forEach(btn => {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.style.opacity = '0.3';
        btn.style.pointerEvents = 'none';
      });
      const removeBtn = itemEl.querySelector('.cart-remove');
      if (removeBtn) removeBtn.style.display = 'none';
    }

    // Disable "+" for welcome kit
    if (window.PumpkinCampaign?.isManaged(item)) { itemEl.querySelectorAll('.cart-remove').forEach(el=>el.remove()); }
    if (isWelcomeKit) {
      const incBtn = itemEl.querySelector('.qty-btn[data-action="increase"]');
      if (incBtn) { incBtn.disabled = true; incBtn.setAttribute('aria-disabled', 'true'); }
      if (item.quantity > 1) enforceWelcomeKitQuantity(data);
    }

    const mountEl = itemEl.querySelector(`#${subCtlId}`);
    if (mountEl) hydrateSubscriptionForItem(mountEl, item);
  });

  itemsContainer.appendChild(fragment);
  const footer = document.getElementById('cart-footer');
  if (footer) footer.style.display = 'flex';
  const totalEl = document.getElementById('cart-total');
  if (totalEl) totalEl.innerText = formatMoney(computeDrawerDisplaySubtotal(data));
  updateHeaderCartCount(getDrawerVisibleItemCount(data));

  // ===== You Saved (below #cart-total) =====
  (function renderYouSavedInline() {
    const totalEl = document.getElementById('cart-total');
    if (!totalEl) return;
    let savedRow = document.getElementById('cart-saved-amount');
    if (!savedRow) {
      savedRow = document.createElement('div');
      savedRow.id = 'cart-saved-amount';
      savedRow.className = 'cart-saved-amount';
      savedRow.style.fontSize = '12px';
      savedRow.style.marginTop = '5px';
      savedRow.style.textAlign = 'right';
      totalEl.parentElement.appendChild(savedRow);
    }
    const savedCents = computeTotalSavings(data);
    if (savedCents > 0) {
      savedRow.textContent = 'You Saved ' + formatMoney(savedCents);
      savedRow.style.display = 'block';
    } else {
      savedRow.style.display = 'none';
    }
  })();

  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      const key = e.currentTarget.dataset.key;
      const action = e.currentTarget.dataset.action;
      const line = (window.__latestCart?.items || []).find(i => i.key === key);
      const welcomeKitConfig = getWelcomeKitConfig();
      const isKit = line && welcomeKitConfig.enabled && line.variant_id === welcomeKitConfig.variantId;
      const currentQty = line?.quantity || 1;

      if (action === 'increase') {
        if (isKit || (line && isFreebieLine(line))) return;
        setLineQuantityOptimistic(key, currentQty + 1);
        return;
      }
      if (currentQty === 1) {
        removeCartItem(key);
      } else {
        setLineQuantityOptimistic(key, Math.max(1, currentQty - 1));
      }
    });
  });

  // Free-gift care package: embedded selection section + per-line variant edit.
  renderGiftDrawer(data).catch(() => {});
}

// ===================== Free-gift care package (drawer) =====================
const GIFT_PROP = '_promo_gift';
const GIFT_RULE_PROP = '_promo_rule_id';
let __giftRuleCache;
const __giftProductCache = new Map();

function giftParseId(raw) {
  if (!raw) return null;
  return String(raw).includes('gid://') ? String(raw).split('/').pop() : (String(raw).replace(/\D/g, '') || null);
}

function giftItemsOf(reward) {
  if (!reward) return [];
  if (Array.isArray(reward.gift_items) && reward.gift_items.length) {
    return reward.gift_items.filter(g => g && (g.product_id || g.variant_id));
  }
  if (reward.gift_product_id || reward.gift_variant_id) {
    return [{ product_id: reward.gift_product_id, variant_id: reward.gift_variant_id, mode: reward.gift_variant_id ? 'AUTO_ADD' : 'CUSTOMER_SELECTS' }];
  }
  return [];
}

function giftRule() {
  if (__giftRuleCache !== undefined) return __giftRuleCache;
  __giftRuleCache = null;
  try {
    const el = document.getElementById('promo-gift-rules');
    if (el) {
      const raw = JSON.parse(el.textContent || '{}');
      const doc = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const rules = Array.isArray(doc.rules) ? doc.rules : [doc];
      const r = rules.find(x => x && x.status !== 'DRAFT' && x.reward && x.reward.type === 'FREE_GIFT' && giftItemsOf(x.reward).length);
      if (r) {
        const claim = r.reward.claim_ui || {};
        __giftRuleCache = {
          id: r.id || 'gift',
          subtotalMin: (r.conditions && r.conditions.subtotal_min) || '',
          title: claim.title || 'Your free care package 🎁',
          button: claim.button_label || 'Add my free gifts',
          items: giftItemsOf(r.reward).map(g => ({
            productId: giftParseId(g.product_id),
            handle: g.product_handle || null,
            variantId: giftParseId(g.variant_id),
            mode: g.mode === 'AUTO_ADD' ? 'AUTO_ADD' : 'CUSTOMER_SELECTS',
            quantity: g.quantity && g.quantity > 0 ? g.quantity : 1,
            label: g.label || '',
          })),
        };
      }
    }
  } catch (e) { /* ignore */ }
  return __giftRuleCache;
}

async function giftProduct(handle) {
  if (!handle) return null;
  if (!__giftProductCache.has(handle)) {
    __giftProductCache.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null)).catch(() => null));
  }
  return __giftProductCache.get(handle);
}

function giftOptionLabel(product) {
  const optionNames = (product?.options || [])
    .map(option => {
      if (typeof option === 'string') return option;
      return option?.name || '';
    })
    .map(name => String(name).trim())
    .filter(name => name && !/^default title$/i.test(name));
  if (!optionNames.length) return 'Select Option';
  return `Select ${optionNames.join(' / ')}`;
}

function giftTitleWithoutFreeSuffix(title) {
  return String(title || '')
    .replace(/\s*(?:[·:|-]\s*)?FREE\s*$/i, '')
    .trim();
}

function giftTitleHtml(title) {
  const cleanTitle = giftTitleWithoutFreeSuffix(title);
  const safeTitle = escapeHtml(cleanTitle || title || 'Free gift');
  return safeTitle;
}

function giftNonGiftSubtotal(cart) {
  return (cart.items || [])
    .filter(i => !(i.properties && i.properties[GIFT_PROP] === 'true'))
    .reduce((s, i) => s + (i.final_line_price ?? i.line_price ?? 0), 0);
}
function giftEligible(cart, rule) {
  if (!rule.subtotalMin) return true;
  const m = parseFloat(rule.subtotalMin);
  return !isFinite(m) || giftNonGiftSubtotal(cart) / 100 >= m;
}
function giftLinesForRule(cart, ruleId) {
  const allGiftLines = (cart.items || []).filter(i => i.properties && i.properties[GIFT_PROP] === 'true');
  if (!ruleId) return allGiftLines;
  const matchingRuleLines = allGiftLines.filter(i => i.properties[GIFT_RULE_PROP] === ruleId);
  return matchingRuleLines.length ? matchingRuleLines : allGiftLines;
}

async function giftAdd(selections, ruleId) {
  const items = selections.filter(s => s.variantId).map(s => ({
    id: Number(s.variantId), quantity: s.quantity || 1,
    properties: { [GIFT_PROP]: 'true', [GIFT_RULE_PROP]: ruleId || 'gift' },
  }));
  if (!items.length) return;
  await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
  fetchCart(true);
}
async function giftSwapVariant(oldKey, newVariantId, ruleId, qty) {
  await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: String(oldKey), quantity: 0 }) });
  await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: qty || 1, properties: { [GIFT_PROP]: 'true', [GIFT_RULE_PROP]: ruleId || 'gift' } }] }) });
  fetchCart(true);
}

async function giftResolveItems(rule) {
  return Promise.all(rule.items.map(async (it) => {
    if (it.mode === 'CUSTOMER_SELECTS' && it.handle) {
      const p = await giftProduct(it.handle);
      if (p) {
        const variants = (p.variants || []).filter(v => v.available !== false).map(v => ({ id: String(v.id), title: v.title }));
        return { kind: variants.length ? 'SELECT' : 'SKIP', title: it.label || p.title, variants, quantity: it.quantity, productId: it.productId, handle: it.handle, optionLabel: giftOptionLabel(p) };
      }
    }
    if (it.variantId) return { kind: 'AUTO', title: it.label || 'Free gift', variantId: it.variantId, quantity: it.quantity };
    return { kind: 'SKIP' };
  }));
}

function giftRowForKey(key) {
  const safeKey = (window.CSS && CSS.escape) ? CSS.escape(key) : key;
  const keyedRow = document.querySelector(`#cart-items [data-line-key="${safeKey}"]`);
  if (keyedRow) return keyedRow.closest('.cart-item');
  const btn = document.querySelector(`#cart-items .qty-btn[data-key="${safeKey}"]`);
  return btn ? btn.closest('.cart-item') : null;
}

// Add a "change size/flavor" <select> onto gift lines already in the cart.
async function addGiftLineEditors(cart, rule) {
  for (const line of giftLinesForRule(cart, rule.id)) {
    const match = rule.items.find(it =>
      it.mode === 'CUSTOMER_SELECTS' &&
      it.handle &&
      (
        String(it.productId || '') === String(line.product_id || '') ||
        String(it.handle || '') === String(line.handle || '') ||
        String(it.variantId || '') === String(line.variant_id || '')
      )
    );
    if (!match) continue;
    const p = await giftProduct(match.handle);
    if (!p) continue;
    const variants = (p.variants || []).filter(v => v.available !== false);
    if (variants.length < 2) continue; // nothing to switch between
    const row = giftRowForKey(line.key);
    if (!row || row.querySelector('.promo-gift-edit')) continue;
    const select = document.createElement('select');
    select.className = 'promo-gift-edit';
    select.setAttribute('aria-label', giftOptionLabel(p));
    variants.forEach(v => {
      const o = document.createElement('option');
      o.value = String(v.id); o.textContent = v.title;
      if (String(v.id) === String(line.variant_id)) o.selected = true;
      select.appendChild(o);
    });
    select.addEventListener('change', () => giftSwapVariant(line.key, select.value, rule.id, line.quantity));
    (row.querySelector('.cart-variant-name') || row.querySelector('.cart-details') || row).appendChild(select);
  }
}

// Embedded selection section (pre-claim) + per-line editors (post-claim).
async function renderGiftDrawer(cart) {
  const rule = giftRule();
  const existing = document.getElementById('promo-gift-drawer-section');
  if (!rule) { if (existing) existing.remove(); return; }

  const claimed = giftLinesForRule(cart, rule.id).length > 0;
  if (claimed) {
    if (existing) existing.remove();
    await addGiftLineEditors(cart, rule);
    return;
  }
  if (!giftEligible(cart, rule)) { if (existing) existing.remove(); return; }

  const resolved = (await giftResolveItems(rule)).filter(r => r.kind === 'AUTO' || r.kind === 'SELECT');
  if (!resolved.length) { if (existing) existing.remove(); return; }

  const prev = {};
  if (existing) existing.querySelectorAll('select[data-gift-idx]').forEach(s => { prev[s.dataset.giftIdx] = s.value; });

  const section = existing || document.createElement('div');
  section.id = 'promo-gift-drawer-section';
  section.className = 'promo-gift-drawer';
  section.innerHTML =
    `<div class="promo-gift-drawer__title">${escapeHtml(rule.title)}</div>` +
    resolved.map((r, idx) => {
      if (r.kind === 'SELECT') {
        const opts = r.variants.map(v => `<option value="${v.id}">${escapeHtml(v.title)}</option>`).join('');
        return `<div class="promo-gift-drawer__row"><span class="promo-gift-drawer__name">${giftTitleHtml(r.title)}</span><select data-gift-idx="${idx}" class="promo-gift-drawer__select" aria-label="${escapeHtml(r.optionLabel || 'Select option')}">${opts}</select></div>`;
      }
      return `<div class="promo-gift-drawer__row"><span class="promo-gift-drawer__name">${giftTitleHtml(r.title)}</span><em class="promo-gift-drawer__incl">Included</em></div>`;
    }).join('') +
    `<button type="button" class="promo-gift-drawer__add">${escapeHtml(rule.button)}</button>`;

  section.querySelectorAll('select[data-gift-idx]').forEach(s => { if (prev[s.dataset.giftIdx]) s.value = prev[s.dataset.giftIdx]; });

  if (!section.parentElement) {
    const cartItems = document.getElementById('cart-items');
    if (cartItems) cartItems.prepend(section);
    else document.getElementById('cart-footer')?.parentElement?.prepend(section);
  }

  section.querySelector('.promo-gift-drawer__add').onclick = async (e) => {
    e.currentTarget.disabled = true;
    const selections = resolved.map((r, idx) => {
      if (r.kind === 'SELECT') {
        const s = section.querySelector(`select[data-gift-idx="${idx}"]`);
        return { variantId: s && s.value, quantity: r.quantity };
      }
      return { variantId: r.variantId, quantity: r.quantity };
    });
    await giftAdd(selections, rule.id);
  };
}

function renderDiscountCTA(activeTier, cartTotal) {
  const cta = document.getElementById('discount-cta');
  if (!cta) return;
  if (activeTier && activeTier.discountCode) {
    const code = activeTier.discountCode.trim();

    // Check sessionStorage first, then cart's applied discount apps (handles cross-session/reload).
    const sessionCode = (sessionStorage.getItem('activeDiscountCode') || '').toUpperCase();
    const cartApps = window.__latestCart?.cart_level_discount_applications || [];
    const alreadyApplied =
      sessionCode === code.toUpperCase() ||
      cartApps.some(app => (app.code || app.title || '').toString().toUpperCase() === code.toUpperCase());

    if (alreadyApplied) {
      cta.innerHTML = `<div class="discount-cta__applied"><strong>${escapeHtml(code)}</strong> applied</div>`;
      return;
    }

    const percent = activeTier.percent || 0;
    const estSavings = percent > 0 ? Math.round(cartTotal * (percent / 100)) : 0;
    cta.innerHTML = `
      <div class="discount-cta__unlocked">
        <button class="discount-cta__btn" data-discount-code="${escapeHtml(code)}">
          Apply ${escapeHtml(code)}${estSavings ? ` (Save ~${formatMoney(estSavings)})` : ''}
        </button>
      </div>`;
    cta.querySelector('.discount-cta__btn')?.addEventListener('click', () => applyDiscountCode(code));
  } else {
    cta.innerHTML = '';
  }
}

function updateProgressBar(cartTotal) {
  const bar = document.querySelector('.progress-bar');
  const text = document.querySelector('.progress-text');
  const thresholdsList = document.querySelector('.progress-thresholds');
  const iconsContainer = document.querySelector('.progress-icons');

  if (thresholdsList) {
    const filteredData = progressData.filter(p => p.label && p.img);
    const itemCount = filteredData.length;
    
    // Remove existing count classes
    thresholdsList.classList.remove('progress-thresholds--1', 'progress-thresholds--2', 'progress-thresholds--3', 'progress-thresholds--4', 'progress-thresholds--5');
    
    // Add class based on item count
    if (itemCount > 0) {
      thresholdsList.classList.add(`progress-thresholds--${itemCount}`);
    }
    
    thresholdsList.innerHTML = filteredData
      .map(p => `
        <li style="display:flex;align-items:center;flex-direction:column;gap:0.25rem;">
          <img src="${p.img}" alt="${escapeHtml(p.label)}">
          <p>${escapeHtml(p.label)}</p>
        </li>`).join('');
  }

  if (iconsContainer) {
    iconsContainer.innerHTML = '';
    const count = progressData.length;
    progressData.forEach((_, i) => {
      const icon = document.createElement('div');
      icon.className = 'milestone-icon';
      const denominator = Math.max(1, count - 1);
      icon.style.left = `${(i / denominator) * 100}%`;
      iconsContainer.appendChild(icon);
    });
  }

  let progress = 0;
  let message = '';

  for (let i = 0; i < progressData.length; i++) {
    const currentRaw = progressData[i];
    const currentThreshold = getCurrentThreshold(currentRaw);
    if (cartTotal < currentThreshold) {
      const prevRaw = progressData[i - 1] || { thresholdRaw: '0' };
      const prevThreshold = getCurrentThreshold(prevRaw);
      const range = Math.max(1, currentThreshold - prevThreshold);
      const offset = cartTotal - prevThreshold;
      progress = ((i + offset / range) / progressData.length) * 100;

      const nextDelta = Math.max(0, currentThreshold - cartTotal);
      message = (currentRaw.message || '')
        .replace('{{next}}', formatMoney(nextDelta))
        .replace('{{label}}', currentRaw.label || '');

      if (bar) bar.style.width = `${progress}%`;
      if (text) text.innerText = message;

      renderDiscountCTA(getActiveDiscountTier(cartTotal), cartTotal);
      handleDiscountAuto(cartTotal);
      return;
    }
  }

  progress = 100;
  message = document.querySelector('.progress-bar-final-message')?.dataset?.message || '';
  if (bar) bar.style.width = `${progress}%`;
  if (text) text.innerText = message;

  renderDiscountCTA(getActiveDiscountTier(cartTotal), cartTotal);
  handleDiscountAuto(cartTotal);
}

// ============================ Auto-swap / remove logic (discount codes) ============================
function handleDiscountAuto(cartTotal) {
  if(window.PumpkinCampaign?.active() && window.PumpkinCampaign?.hasReward()) return;
  const redirecting = sessionStorage.getItem('discountRedirecting') === '1';
  if (redirecting) return;
  const activeTier = getActiveDiscountTier(cartTotal);
  const currentCode = sessionStorage.getItem('activeDiscountCode') || '';
  if (activeTier && activeTier.discountCode) {
    if (AUTO_SWAP_DISCOUNT && currentCode && currentCode !== activeTier.discountCode) {
      applyDiscountCode(activeTier.discountCode);
      return;
    }
    return;
  }
  if (!activeTier && currentCode && AUTO_CLEAR_ON_DROP) {
    clearDiscountCode();
  }
}

// ============================ Build Subscribe Toggle ============================
// NOTE: price pill below is now LINE-TOTAL aware (qty * unit)
function buildSubscriptionControl({ item, plans }) {
  const isSubscribed =
    !!(item.selling_plan_allocation &&
       item.selling_plan_allocation.selling_plan &&
       String(item.selling_plan_allocation.selling_plan.id));

  const wrapper = document.createElement('div');
  wrapper.className = 'subscription-control';

  const qty = Math.max(1, item.quantity || 1);

  // Helpers
  const makePlanSelect = (selectedId = '') => {
    const select = document.createElement('select');
    select.className = 'subscription-select compact';
    plans.forEach(p => {
      const opt = document.createElement('option');
      opt.value = String(p.id);
      opt.textContent = p.name; // "30 days", "4 weeks", etc.
      select.appendChild(opt);
    });
    select.value = String(selectedId || plans[0]?.id || '');
    return select;
  };

  const renderPricePill = ({ subCents, baseCents }) => {
    const pill = document.createElement('div');
    pill.className = 'sub-price-pill';
    const subHtml = `<span class="pill-discounted">${formatMoney(subCents)}</span>`;
    const baseHtml = baseCents != null ? `<span class="pill-original">${formatMoney(baseCents)}</span>` : '';
    pill.innerHTML = `${subHtml}${baseHtml}`;
    return pill;
  };

  // Preview for OFF state (based on max percent found in plans)
  const nonSubMeta = computeSubscriptionPrices(item, window.__latestCart || null);
  const baseUnit = nonSubMeta.baseUnit; // unit OT price
  const maxPercent = extractMaxPercentFromPlans(plans);
  const previewSubUnit = baseUnit != null ? Math.round(baseUnit * (1 - maxPercent / 100)) : null;
  const previewBaseLine = baseUnit != null ? baseUnit * qty : null;
  const previewSubLine  = previewSubUnit != null ? previewSubUnit * qty : null;

  // --- UI skeleton
  const row = document.createElement('div');
  row.className = 'sub-toggle-row';

  const toggleWrap = document.createElement('label');
  toggleWrap.className = 'sub-toggle';

  const toggleInput = document.createElement('input');
  toggleInput.type = 'checkbox';
  toggleInput.className = 'sub-toggle-input';
  toggleInput.checked = isSubscribed;

  const toggleTrack = document.createElement('span');
  toggleTrack.className = 'sub-toggle-track';

  const toggleLabel = document.createElement('span');
  toggleLabel.className = 'sub-toggle-label';
  toggleLabel.textContent = 'Subscribe & Save';

  toggleWrap.appendChild(toggleInput);
  toggleWrap.appendChild(toggleTrack);
  toggleWrap.appendChild(toggleLabel);

  const pricePill = document.createElement('div');
  pricePill.className = 'sub-toggle-price';

  const planWrap = document.createElement('div');
  planWrap.className = 'sub-plan-wrap';

  const benefit = document.createElement('div');
  benefit.className = 'subscription-message';
  benefit.textContent = '*25% off first shipment, 15% thereafter';

  row.appendChild(toggleWrap);
  row.appendChild(pricePill);

  wrapper.appendChild(row);
  wrapper.appendChild(planWrap);
  wrapper.appendChild(benefit);

  // ----- Renderers per state (LINE TOTALS)
  const setStateSubscribed = (sellingPlanId) => {
    // Use current cart meta (unit) then convert to line totals
    const meta = computeSubscriptionPrices(item, window.__latestCart || null);
    const baseLine = (meta.baseUnit != null) ? meta.baseUnit * qty : null;
    const subLine  = (meta.subUnit  != null) ? meta.subUnit  * qty : null;

    pricePill.innerHTML = '';
    pricePill.appendChild(renderPricePill({
      subCents: subLine != null ? subLine : (previewSubLine || 0),
      baseCents: baseLine
    }));

    planWrap.innerHTML = '';
    const select = makePlanSelect(sellingPlanId);
    planWrap.appendChild(select);
    planWrap.style.display = 'block';
    benefit.style.display = 'block';

    select.addEventListener('change', async () => {
      const chosen = select.value || plans[0]?.id || '';
      if (!chosen) return;
      wrapper.classList.add('is-updating');
      try {
        await convertLineSubscription({
          lineKey: item.key,
          variantId: item.variant_id,
          quantity: item.quantity,
          sellingPlanId: String(chosen)
        });
      } finally {
        // rehydrated via fetchCart
      }
    });
  };

  const setStateOneTime = () => {
    pricePill.innerHTML = '';
    if (previewSubLine != null && previewBaseLine != null) {
      const pill = renderPricePill({
        subCents: previewSubLine,
        baseCents: previewBaseLine
      });
      const tag = document.createElement('span');
      tag.className = 'pill-tag';
      tag.textContent = `Save ${maxPercent}%`;
      pill.appendChild(tag);
      pricePill.appendChild(pill);
    }
    planWrap.style.display = 'none';
    planWrap.innerHTML = '';
    benefit.style.display = 'none';
  };

  // ----- Initialize
  if (isSubscribed) {
    const currentPlanId = String(item.selling_plan_allocation.selling_plan.id);
    setStateSubscribed(currentPlanId);
  } else {
    setStateOneTime();
  }

  // ----- Toggle handler
  toggleInput.addEventListener('change', async () => {
    const turningOn = toggleInput.checked;
    if (!plans.length) { toggleInput.checked = !turningOn; return; }
    wrapper.classList.add('is-updating');
    try {
      if (turningOn) {
        const firstPlan = String(plans[0].id);
        await convertLineSubscription({
          lineKey: item.key,
          variantId: item.variant_id,
          quantity: item.quantity,
          sellingPlanId: firstPlan
        });
        setStateSubscribed(firstPlan);
      } else {
        await convertLineSubscription({
          lineKey: item.key,
          variantId: item.variant_id,
          quantity: item.quantity,
          sellingPlanId: null
        });
        setStateOneTime();
      }
    } finally {
      // full re-render via fetchCart
    }
  });

  return wrapper;
}

function hydrateSubscriptionForItem(containerEl, item) {
  if (HIDE_SUBSCRIBE_SAVE) return;
  if (item.properties && (item.properties.free_product || item.properties.free || item.properties.gift || item.properties.welcome_kit)) return;
  const handle = item.handle;
  if (!handle) return;
  if (SUBSCRIBE_SAVE_EXCLUDED_HANDLES.includes(handle)) return;
  fetchProductPlans(handle).then(plans => {
    if (!plans.length) return;
    const control = buildSubscriptionControl({ item, plans });
    containerEl.appendChild(control);
  });
}

// ============================ Freebie Variant Selector ============================
function buildFreebieVariantControl({ item, product }) {
  const variants = Array.isArray(product?.variants) ? product.variants.filter(v => v && v.available) : [];
  if (variants.length <= 1) return null;

  const wrapper = document.createElement('div');
  wrapper.className = 'freebie-variant-control';

  const label = document.createElement('label');
  label.className = 'freebie-variant-label';
  label.textContent = 'Choose your gift option';

  const select = document.createElement('select');
  select.className = 'freebie-variant-select';

  variants.forEach(v => {
    const opt = document.createElement('option');
    opt.value = String(v.id);
    opt.textContent = v.title;
    if (v.id === item.variant_id) opt.selected = true;
    select.appendChild(opt);
  });

  select.addEventListener('change', async () => {
    const newId = parseInt(select.value, 10);
    if (!newId || newId === item.variant_id) return;
    wrapper.classList.add('is-updating');
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });

      const props = Object.assign({}, item.properties || {}, { free_product: true });

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newId,
          quantity: item.quantity || 1,
          properties: props
        })
      }).then(r => r.json()).catch(()=>{});

      fetchCart(true);
    } finally {
      // UI will be fully re-rendered by fetchCart
    }
  });

  wrapper.appendChild(label);
  wrapper.appendChild(select);
  return wrapper;
}

function hydrateFreebieVariantSelector(containerEl, item) {
  if (!item || !item.handle) return;
  fetchProductJson(item.handle).then(product => {
    if (!product) return;
    const control = buildFreebieVariantControl({ item, product });
    if (control) containerEl.appendChild(control);
  });
}

// ============================ Cart fetch / actions ============================
async function runFetchCart(retry = false) {
  await window.flushCartQuantityChanges?.();
  let baseCart = await fetch('/cart.js').then(r => r.json());


  window.__latestCart = baseCart;
  updateCartUI(baseCart); updateProgressBar(baseCart.total_price);
  await enforceWelcomeKitQuantity(baseCart);
  const removedOnlyKit = await removeWelcomeKitIfCartHasOnlyKit(baseCart);
  await maybeRemoveWelcomeKitIfNoSubscriptions(baseCart);

  let current = baseCart;

  if (removedOnlyKit) {
    current = await fetch('/cart.js').then(r => r.json());
    if (current.item_count === 0) {
      window.__latestCart = current;
      updateCartUI(current);
      updateProgressBar(current.total_price);
      if (retry && current.item_count === 0) setTimeout(() => fetchCart(false), 500);
      return current;
    }
  }

  if (cartNeedsBogoReconcile(current) || cartThresholdFreebiesNeedsSync(current)) {
    showCartItemsLoading();
  }

  const afterThreshold = await syncThresholdFreebies(current);
  let reconciledCart = await reconcileBogoFreeLines(afterThreshold);
  if(window.PumpkinCampaign) {
    try { reconciledCart = await window.PumpkinCampaign.reconcile(); }
    catch (error) {
      // Optional gifts must not hide paid merchandise when verification fails.
      try {
        const response = await fetch('/cart.js', {signal: AbortSignal.timeout(15000), cache:'no-store'});
        if(response.ok) reconciledCart = await response.json();
      } catch {}
      window.__latestCart = reconciledCart;
      updateCartUI(reconciledCart); updateProgressBar(reconciledCart.total_price);
      reportDrawerError(error);
      return reconciledCart;
    }
  }

  window.__latestCart = reconciledCart;
  await preloadPromoVariantCatalog(reconciledCart.items);
  if (BOGO_DRAWER_ENABLED) await preloadBogoVariantCatalog(reconciledCart.items);
  updateCartUI(reconciledCart);
  updateProgressBar(reconciledCart.total_price);

  if (retry && reconciledCart.item_count === 0) {
    setTimeout(() => fetchCart(false), 500);
  }
  return reconciledCart;
}

function fetchCart(retry = false) {
  if (__fetchCartInFlight__) return __fetchCartInFlight__;
  __fetchCartInFlight__ = runFetchCart(retry).finally(() => {
    __fetchCartInFlight__ = null;
  });
  return __fetchCartInFlight__;
}

function removeCartItem(key) {
  clearTimeout(__qtySyncTimers[key]); delete __qtySyncTimers[key]; delete __qtyPending[key];
  if(window.PumpkinCampaign) return window.PumpkinCampaign.change(key,0).then(c=>{window.__latestCart=c;updateCartUI(c);updateProgressBar(c.total_price);}).catch(e=>window.PumpkinCampaign.showError(e));
  fetch('/cart/change.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  }).then(() => fetchCart());
}
function updateQuantity(key, qty) {
  if(window.PumpkinCampaign) return window.PumpkinCampaign.change(key,Math.max(0,qty)).then(c=>{window.__latestCart=c;updateCartUI(c);updateProgressBar(c.total_price);}).catch(e=>window.PumpkinCampaign.showError(e));
  const line = (window.__latestCart?.items || []).find(i => i.key === key);
  // BOGO free split-lines are managed exclusively by reconcileBogoFreeLines
  if (line && isBogoFreeLine(line)) return;
  const welcomeKitConfig = getWelcomeKitConfig();
  if (line && welcomeKitConfig.enabled && line.variant_id === welcomeKitConfig.variantId) {
    qty = Math.min(1, Math.max(0, qty));
  }
  if (line && isFreebieLine(line)) {
    qty = Math.min(1, Math.max(0, qty));
  }
  if (qty < 1) qty = 0;

  fetch('/cart/change.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty })
  }).then(() => fetchCart());
}

// Per-key debounce + sequence guard for background server sync of qty changes.
const __qtySyncTimers = Object.create(null);
const __qtyPending = Object.create(null);
const __qtyTasks = new Set();
function reportDrawerError(error) {
  if (window.PumpkinCampaign) window.PumpkinCampaign.showError(error);
  const drawer=document.getElementById('cart-drawer');
  if(drawer){let el=drawer.querySelector('[data-storefront-cart-error]');if(!el){el=document.createElement('p');el.dataset.storefrontCartError='';el.setAttribute('role','alert');el.style.cssText='padding:12px 20px;color:#9b1c1c';drawer.querySelector('.cart-drawer-header, .cart-header')?.after(el);if(!el.parentNode)drawer.prepend(el);}el.textContent=error.message || 'Your cart could not be updated. Please wait and try again.';}
  else {
    let el=document.getElementById('storefront-cart-error');
    if(!el){el=document.createElement('p');el.id='storefront-cart-error';el.setAttribute('role','alert');document.querySelector('#cart-drawer .cart-items')?.before(el);}
    el.textContent=error.message || 'Your cart could not be updated. Please wait and try again.';
  }
}
function settleDrawerQuantity(key) {
  clearTimeout(__qtySyncTimers[key]); delete __qtySyncTimers[key];
  if (!(key in __qtyPending)) return Promise.resolve();
  const quantity = __qtyPending[key]; delete __qtyPending[key];
  const work = window.PumpkinCampaign
    ? window.PumpkinCampaign.change(key, quantity)
    : fetch('/cart/change.js', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:key,quantity})}).then(async r=>{const cart=await r.json();if(!r.ok)throw Error(cart.description||'Cart update failed');return cart;});
  const task = work.then(cart => {
    if (Object.keys(__qtyPending).length || __qtyTasks.size > 1) return;
    window.__latestCart = cart;
    updateCartUI(cart); updateProgressBar(cart.total_price);
  }).catch(async error => {
    // Recover the authoritative quantity after stock/network rejection.
    try { const r=await fetch('/cart.js'); if(r.ok){const cart=await r.json();window.__latestCart=cart;updateCartUI(cart);updateProgressBar(cart.total_price);} } catch {}
    throw error;
  }).finally(()=>__qtyTasks.delete(task));
  __qtyTasks.add(task); return task;
}
window.flushCartQuantityChanges = async function() {
  const pending = Object.keys(__qtyPending).map(settleDrawerQuantity);
  await Promise.all([...__qtyTasks, ...pending]);
};


/**
 * Optimistically change a cart line's quantity in the drawer.
 * Updates the qty number + that line's total + the cart subtotal IN PLACE
 * (no full re-render, no loading flicker, line price is not reloaded), then
 * syncs the server in the background (debounced, last-write-wins).
 */
function setLineQuantityOptimistic(key, newQty) {

  const cart = window.__latestCart;
  const line = cart && (cart.items || []).find(i => i.key === key);
  // No cached cart yet — fall back to the original (re-rendering) path.
  if (!cart || !line) return updateQuantity(key, newQty);

  // Same guards as updateQuantity.
  if (isBogoFreeLine(line)) return;
  const welcomeKitConfig = getWelcomeKitConfig();
  if (welcomeKitConfig.enabled && line.variant_id === welcomeKitConfig.variantId) {
    newQty = Math.min(1, Math.max(0, newQty));
  }
  if (isFreebieLine(line)) newQty = Math.min(1, Math.max(0, newQty));

  if (newQty < 1) { removeCartItem(key); return; }

  const oldQty = Math.max(1, line.quantity || 1);
  if (newQty === oldQty) return;
  const ratio = newQty / oldQty;

  // Scale line totals; keep per-unit prices untouched so buildLinePriceHTML
  // re-derives the correct line total for the new quantity.
  ['final_line_price', 'line_price', 'original_line_price'].forEach(f => {
    if (typeof line[f] === 'number') line[f] = Math.round(line[f] * ratio);
  });
  line.quantity = newQty;

  // Recompute cart aggregates used by the subtotal / progress / count.
  cart.total_price = (cart.items || []).reduce(
    (s, i) => s + (typeof i.final_line_price === 'number' ? i.final_line_price : (i.line_price || 0)), 0
  );
  cart.item_count = (cart.items || []).reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  // --- In-place DOM update for just this row (no full re-render) ---
  const row = [...document.querySelectorAll('#cart-items .cart-item')].find(el => {
    const b = el.querySelector('.qty-btn[data-key]');
    return b && b.dataset.key === key;
  });
  if (row) {
    row.querySelectorAll('.cart-quantity').forEach(el => { el.textContent = String(newQty); });
    const priceEl = row.querySelector('.cart-price');
    if (priceEl) {
      const badge = priceEl.querySelector('.save-badge');
      const { html } = buildLinePriceHTML(line, cart);
      priceEl.innerHTML = html + (badge ? ' ' + badge.outerHTML : '');
    }
  }

  // Subtotal must keep updating on qty change.
  const totalEl = document.getElementById('cart-total');
  if (totalEl) totalEl.innerText = formatMoney(computeDrawerDisplaySubtotal(cart));
  const savedRow = document.getElementById('cart-saved-amount');
  if (savedRow) {
    const saved = computeTotalSavings(cart);
    if (saved > 0) { savedRow.textContent = 'You Saved ' + formatMoney(saved); savedRow.style.display = 'block'; }
    else savedRow.style.display = 'none';
  }
  updateHeaderCartCount(getDrawerVisibleItemCount(cart));
  if (typeof updateProgressBar === 'function') updateProgressBar(cart.total_price);

  // Keep the original in-place response; settle the final quantity once per pause.
  clearTimeout(__qtySyncTimers[key]);
  __qtyPending[key] = newQty;
  __qtySyncTimers[key] = setTimeout(() => settleDrawerQuantity(key).catch(reportDrawerError), 350);

}

function sanitizePromoGiftPopupFreeLabels(root = document) {
  const modal = root.matches?.('#promo-gift-overlay, .promo-gift-modal')
    ? root
    : (root.querySelector?.('#promo-gift-overlay, .promo-gift-modal') || null);
  if (!modal) return;
  const walker = document.createTreeWalker(modal, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach(node => {
    node.nodeValue = String(node.nodeValue || '').replace(
      /(\bFree\b[^·:|\-\n]+?)\s*(?:[·:|\-]\s*)FREE\b/gi,
      '$1'
    );
  });
}

function watchPromoGiftPopupLabels() {
  sanitizePromoGiftPopupFreeLabels();
  new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) sanitizePromoGiftPopupFreeLabels(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
}

// ============================ Boot ============================
window.addEventListener('DOMContentLoaded', () => {
  updateHeaderCartCount(0);
  watchPromoGiftPopupLabels();

  if (sessionStorage.getItem('discountRedirecting') === '1') sessionStorage.removeItem('discountRedirecting');
  if (sessionStorage.getItem('reopenDrawer') === '1') { sessionStorage.removeItem('reopenDrawer'); openCartDrawer(); }

  fetchCart().catch(e=>window.PumpkinCampaign?.showError(e));

  document.getElementById('cart-close')?.addEventListener('click', closeCartDrawer);
  document.querySelector('.cart-drawer-overlay')?.addEventListener('click', closeCartDrawer);

  // Intercept add-to-cart forms
  document.querySelectorAll('form[action^="/cart/add"]').forEach(form => {
    form.addEventListener('submit', e => {
      const idInput = form.querySelector('input[name="id"]');
      const targetId = idInput && Number(idInput.value);
      const welcomeKitConfig = getWelcomeKitConfig();
      if (welcomeKitConfig.enabled && targetId === welcomeKitConfig.variantId) {
        const hasKit = !!(window.__latestCart?.items || []).some(i => i.variant_id === welcomeKitConfig.variantId);
        if (hasKit) {
          e.preventDefault();
          openCartDrawer();
          return;
        }
      }

      e.preventDefault();
      if (form.dataset.cartSubmitting === 'true') return;
      form.dataset.cartSubmitting = 'true';
      const submit=form.querySelector('[type="submit"]');
      if(submit){submit.disabled=true;submit.setAttribute('aria-busy','true');}
      document.querySelectorAll('[data-storefront-cart-error]').forEach(el=>el.remove());
      const formData = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: formData, signal: AbortSignal.timeout(15000) }).then(async response => {
        const result = await response.json();
        if (!response.ok) throw Error(result.description || result.message || 'Unable to add this item.');
        openCartDrawer();
      }).catch(error => { openCartDrawer(); reportDrawerError(error); }).finally(()=>{
        delete form.dataset.cartSubmitting;
        if(submit){submit.disabled=false;submit.removeAttribute('aria-busy');}
      });
    });
  });

  document.querySelectorAll('.cart-drawer-button').forEach(icon => {
    icon.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation(); openCartDrawer(); return false;
    });
  });

});
